import { matchPath } from "react-router-dom";

const GA_SCRIPT_ID = "ftmc-ga4-script";
const PII_BLOCKLIST = new Set([
  "email",
  "name",
  "tenantid",
  "tenant_id",
  "userid",
  "user_id",
  "accountid",
  "account_id",
]);

const ROUTE_PATTERNS = [
  { path: "/login", routeTemplate: "login" },
  { path: "/projects/:projectPublicId", routeTemplate: "project_detail" },
  { path: "/projects", routeTemplate: "projects" },
  { path: "/utilities/:utilityId", routeTemplate: "utilities" },
  { path: "/utilities", routeTemplate: "utilities" },
  { path: "/equipment/:machineId", routeTemplate: "equipment" },
  { path: "/equipment", routeTemplate: "equipment" },
  { path: "/fertilizers/:mixId", routeTemplate: "fertilizers" },
  { path: "/fertilizers", routeTemplate: "fertilizers" },
  { path: "/excel", routeTemplate: "excel_exchange" },
  { path: "/dashboard", routeTemplate: "dashboard" },
  { path: "/profile", routeTemplate: "profile" },
  { path: "/farm/form", routeTemplate: "farm_form" },
  { path: "/farm/:farmId/form", routeTemplate: "farm_form" },
  { path: "/addfarm", routeTemplate: "farm_form" },
  { path: "/farm/:farmId/editfarm", routeTemplate: "farm_form" },
  { path: "/farm/:farmId/field/form", routeTemplate: "field_form" },
  { path: "/farm/:farmId/addfield", routeTemplate: "field_form" },
  { path: "/farm/:farmId/field/:fieldId/form", routeTemplate: "field_form" },
  { path: "/farm/:farmId/field/:fieldId/editfield", routeTemplate: "field_form" },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/analysis",
    routeTemplate: "analysis",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/addanalysis",
    routeTemplate: "review",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/rotation",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/field-operations/cultivation",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/field-operations",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/harvest",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId/editcropyear",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear/:cropyearId",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/cropyear",
    routeTemplate: "cropyear_edit",
  },
  {
    path: "/farm/:farmId/field/:fieldId/addcropyear",
    routeTemplate: "cropyear_edit",
  },
  { path: "/farm/:farmId/field/:fieldId", routeTemplate: "field_detail" },
  { path: "/farm/:farmId", routeTemplate: "farm_detail" },
  { path: "/growers", routeTemplate: "portal_workspace" },
  { path: "/delegations", routeTemplate: "portal_workspace" },
  { path: "/imports", routeTemplate: "portal_workspace" },
  { path: "/reconciliation", routeTemplate: "portal_workspace" },
  { path: "/persona", routeTemplate: "portal_workspace" },
];

let analyticsContext = {};
let analyticsUserId;
let initializedMeasurementId = null;
let scriptLoadPromise = null;

function readEnv(name, fallback = "") {
  const value = process.env?.[name];

  if (typeof value !== "string") {
    return fallback;
  }

  return value.trim() || fallback;
}

function isTruthyFlag(value) {
  return String(value || "").trim().toLowerCase() === "true";
}

export function getAnalyticsSettings() {
  const measurementId = readEnv("REACT_APP_GA_MEASUREMENT_ID");
  const gaEnabled = isTruthyFlag(readEnv("REACT_APP_GA_ENABLED"));
  const debugMode = isTruthyFlag(readEnv("REACT_APP_GA_DEBUG"));
  const isProduction = process.env.NODE_ENV === "production";
  const enabled = Boolean(
    measurementId && gaEnabled && (isProduction || debugMode)
  );

  return {
    enabled,
    measurementId,
    debugMode,
    isProduction,
  };
}

export function shouldTrackPageView(pathname = "") {
  return ![
    "/",
    "/invitation",
    "/registration",
    "/forgot_password",
    "/password_reset",
  ].includes(pathname);
}

export function getRouteTemplate(pathname = "", hostPortal = "grower") {
  if (pathname === "/dashboard" && hostPortal !== "grower") {
    return "portal_dashboard";
  }

  const matchedRoute = ROUTE_PATTERNS.find(({ path }) =>
    matchPath({ path, end: true }, pathname)
  );

  return matchedRoute?.routeTemplate || "not_found";
}

function toGaValue(value) {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }

  return value;
}

function stripPII(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([rawKey]) => {
      const key = String(rawKey || "").replace(/\s+/g, "").toLowerCase();
      return !PII_BLOCKLIST.has(key);
    })
  );
}

function sanitizeParams(params = {}) {
  return Object.fromEntries(
    Object.entries(stripPII(params))
      .map(([key, value]) => [key, toGaValue(value)])
      .filter(([, value]) => value !== undefined)
  );
}

function ensureWindowGtag() {
  if (typeof window === "undefined") {
    return null;
  }

  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  return window.gtag;
}

function loadGoogleTag(measurementId) {
  if (typeof document === "undefined") {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(GA_SCRIPT_ID);

  if (existingScript) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Analytics"));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

function getCurrentRouteTemplate() {
  if (typeof window === "undefined") {
    return undefined;
  }

  return getRouteTemplate(window.location.pathname, analyticsContext.host_portal);
}

function buildConfigParams(settings) {
  const config = {
    send_page_view: false,
    debug_mode: settings.debugMode,
  };

  if (analyticsUserId !== undefined) {
    config.user_id = analyticsUserId;
  }

  return config;
}

export function initAnalytics() {
  const settings = getAnalyticsSettings();

  if (!settings.enabled || typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (initializedMeasurementId === settings.measurementId) {
    return Promise.resolve(true);
  }

  const gtag = ensureWindowGtag();

  if (!gtag) {
    return Promise.resolve(false);
  }

  gtag("js", new Date());
  gtag("config", settings.measurementId, buildConfigParams(settings));
  initializedMeasurementId = settings.measurementId;

  return loadGoogleTag(settings.measurementId)
    .then(() => true)
    .catch((error) => {
      console.error("[analytics] Failed to initialize GA4", error);
      return false;
    });
}

export function setAnalyticsUserId(nextUserId) {
  if (typeof nextUserId === "string") {
    analyticsUserId = nextUserId.trim() || null;
  } else if (nextUserId === null) {
    analyticsUserId = null;
  } else {
    analyticsUserId = undefined;
  }

  const settings = getAnalyticsSettings();
  const gtag = typeof window !== "undefined" ? window.gtag : null;

  if (!settings.enabled || typeof gtag !== "function" || !settings.measurementId) {
    return analyticsUserId;
  }

  gtag("config", settings.measurementId, buildConfigParams(settings));
  return analyticsUserId;
}

export function setAnalyticsContext(nextContext = {}) {
  analyticsContext = sanitizeParams({
    ...analyticsContext,
    ...nextContext,
  });

  const { enabled } = getAnalyticsSettings();
  const gtag = typeof window !== "undefined" ? window.gtag : null;

  if (!enabled || typeof gtag !== "function") {
    return analyticsContext;
  }

  gtag("set", "user_properties", analyticsContext);
  return analyticsContext;
}

export function trackPageView({
  routeTemplate,
  hostPortal,
  is_impersonating: isImpersonating,
  personaPortal,
  isAuthenticated,
} = {}) {
  const { enabled } = getAnalyticsSettings();
  const gtag = typeof window !== "undefined" ? window.gtag : null;

  if (!enabled || typeof gtag !== "function" || typeof window === "undefined") {
    return false;
  }

  const params = sanitizeParams({
    page_title: document.title,
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    route_template:
      routeTemplate || getRouteTemplate(window.location.pathname, hostPortal),
    host_portal: hostPortal,
    is_impersonating: isImpersonating,
    persona_portal: personaPortal,
    is_authenticated: isAuthenticated,
  });

  gtag("event", "page_view", params);
  return true;
}

export function trackEvent(name, params = {}) {
  const { enabled } = getAnalyticsSettings();
  const gtag = typeof window !== "undefined" ? window.gtag : null;

  if (!enabled || typeof gtag !== "function" || !name) {
    return false;
  }

  const mergedParams = sanitizeParams({
    ...analyticsContext,
    route_template: params.route_template || getCurrentRouteTemplate(),
    ...params,
  });

  gtag("event", name, mergedParams);
  return true;
}
